/**
 * Find Consultant — component.
 * One `FindConsultantSection` per `.find-consultant-section` (near-me + by-name search, contact modal).
 * Strings: `window.findConsultantFormStrings`
 *
 * @class FindConsultantComponent
 * @class FindConsultantSection
 */
const NEAR_ME_PATH = '/apps/public/consultants/near-me';
/** Same contract as storefront consultant search: GET with firstName, lastName, province. */
const FIND_CONSULTANTS_PATH = '/apps/public/consultants/find';
const CONTACT_CONSULTANT_PATH = '/apps/public/contact-consultant';

class FindConsultantComponent {
  /** @returns {Record<string, string>} */
  static get strings() {
    return window.findConsultantFormStrings || {};
  }

  static buildConsultantFindRequestUrl(params) {
    const custom = globalThis.__watkinsConsultantFindUrl;
    if (typeof custom === 'function') {
      return custom(params);
    }
    if (typeof custom === 'string' && custom.indexOf('{query}') !== -1) {
      return custom.split('{query}').join(params.toString());
    }
    return FIND_CONSULTANTS_PATH + '?' + params.toString();
  }

  static buildContactConsultantUrl() {
    const custom = globalThis.__watkinsContactConsultantUrl;
    if (typeof custom === 'string' && custom.trim()) {
      return custom.trim();
    }
    return CONTACT_CONSULTANT_PATH;
  }

  static pickConsultantEmailFromObject(o) {
    if (o == null || typeof o !== 'object') return '';
    const e =
      o.email ||
      o.Email ||
      o.consultantEmail ||
      o.ConsultantEmail ||
      o.consultant_email ||
      o.workEmail ||
      o.WorkEmail ||
      o.businessEmail ||
      o.BusinessEmail;
    return e != null && String(e).trim() ? String(e).trim() : '';
  }

  /**
   * When customer-accounts-form.js is not on the page, parse the same response shapes as
   * CustomerAccountsForm.extractConsultantRowsFromFindApiResponse (simplified row mapping).
   */
  static normalizeFindConsultantList(data) {
    let list = null;
    if (Array.isArray(data)) {
      list = data;
    } else if (data && typeof data === 'object') {
      if (Array.isArray(data.data)) list = data.data;
      else if (Array.isArray(data.consultants)) list = data.consultants;
      else if (Array.isArray(data.results)) list = data.results;
      else if (Array.isArray(data.items)) list = data.items;
      else if (Array.isArray(data.value)) list = data.value;
      else if (Array.isArray(data.Value)) list = data.Value;
      else if (data.consultant != null && typeof data.consultant === 'object' && !Array.isArray(data.consultant)) {
        list = [data.consultant];
      }
    }
    return list && list.length ? list : [];
  }

  static findApiRowFromItem(item, searchProvinceHint) {
    if (item == null || typeof item !== 'object') return null;
    const o = item.consultant || item.Consultant || item;
    let first = String(o.firstName || o.first_name || o.FirstName || '').trim();
    let last = String(
      o.lastName || o.last_name || o.LastName || o.businessName || o.BusinessName || ''
    ).trim();
    const combined = String(o.name || o.Name || o.fullName || o.FullName || '').trim();
    if (!first && !last && combined) {
      const parts = combined.split(/\s+/);
      first = parts[0] || '';
      last = parts.slice(1).join(' ') || '';
    }
    let displayName = [first, last].filter(Boolean).join(' ').trim();
    if (!displayName && combined) displayName = combined;
    const code =
      o.consultantCode != null && String(o.consultantCode).trim() !== ''
        ? String(o.consultantCode).trim()
        : o.consultantcode != null && String(o.consultantcode).trim() !== ''
          ? String(o.consultantcode).trim()
          : typeof o.code === 'string' && o.code.trim() !== ''
            ? o.code.trim()
            : '';
    if (!displayName && code) {
      displayName = code.replace(/^#+/, '');
    }
    if (!displayName) displayName = '—';
    let stateCode = String(
      o.state || o.province || o.stateProvince || o.region || o.State || ''
    ).trim();
    if (!stateCode && searchProvinceHint) stateCode = String(searchProvinceHint).trim();
    if (displayName === '—' && (!stateCode || stateCode === '—') && !code) return null;
    return {
      displayName: displayName,
      stateCode: stateCode || '—',
      email: FindConsultantComponent.pickConsultantEmailFromObject(o),
    };
  }

  static extractFindApiRows(data, searchProvinceHint) {
    if (
      typeof CustomerAccountsForm !== 'undefined' &&
      typeof CustomerAccountsForm.extractConsultantRowsFromFindApiResponse === 'function'
    ) {
      return CustomerAccountsForm.extractConsultantRowsFromFindApiResponse(data, searchProvinceHint);
    }
    const list = FindConsultantComponent.normalizeFindConsultantList(data);
    const out = [];
    for (let i = 0; i < list.length; i++) {
      const row = FindConsultantComponent.findApiRowFromItem(list[i], searchProvinceHint);
      if (row) out.push(row);
    }
    return out;
  }

  static mapFindApiRowsToContactRows(apiRows) {
    return apiRows.map(function (r) {
      const st = r.stateCode != null && r.stateCode !== '—' ? String(r.stateCode) : '';
      const em =
        r.email != null && String(r.email).trim()
          ? String(r.email).trim()
          : r.consultantEmail != null && String(r.consultantEmail).trim()
            ? String(r.consultantEmail).trim()
            : '';
      return { name: r.displayName || '—', state: st, email: em };
    });
  }

  static clearByNameFormValidity(form) {
    if (!form) return;
    form.querySelectorAll('input, select').forEach(function (inp) {
      inp.setCustomValidity('');
    });
  }

  static escapeHtml(str) {
    if (str == null) return '';
    const d = document.createElement('div');
    d.textContent = String(str);
    return d.innerHTML;
  }

  static escapeAttr(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;');
  }

  static showResultsShellForContainer(container) {
    const shell = container.closest('.find-consultant-results-shell');
    if (!shell) return;
    shell.hidden = false;
    shell.setAttribute('aria-hidden', 'false');
  }

  static hideResultsShellForContainer(container) {
    const shell = container.closest('.find-consultant-results-shell');
    if (!shell) return;
    shell.hidden = true;
    shell.setAttribute('aria-hidden', 'true');
  }

  /** When there are more than six result rows, cap height and scroll so the block stays compact. */
  static updateResultsScrollState(container) {
    if (!container) return;
    container.classList.remove('find-consultant-results--scrollable');
    container.style.maxHeight = '';
    const rows = container.querySelectorAll('.find-consultant-result-row');
    if (rows.length <= 6) return;
    const cRect = container.getBoundingClientRect();
    const sixth = rows[5];
    if (!sixth) return;
    const maxPx = Math.ceil(sixth.getBoundingClientRect().bottom - cRect.top);
    if (maxPx < 48) return;
    container.style.maxHeight = maxPx + 'px';
    container.classList.add('find-consultant-results--scrollable');
  }

  static renderResultRows(container, rows, options) {
    options = options || {};
    const showDemoNote = options.showDemoNote !== false;
    const S = FindConsultantComponent.strings;
    FindConsultantComponent.showResultsShellForContainer(container);
    container.innerHTML = '';

    if (showDemoNote && S.demoNote) {
      const note = document.createElement('p');
      note.className = 'small text-muted mb-5';
      note.textContent = S.demoNote;
      container.appendChild(note);
    }

    rows.forEach(function (row) {
      const wrap = document.createElement('div');
      wrap.className = 'find-consultant-result-row';
      const nameEsc = FindConsultantComponent.escapeHtml(row.name);
      const stateEsc = FindConsultantComponent.escapeHtml(row.state);
      wrap.innerHTML =
        '<div class="find-consultant-result-row__inner">' +
        '<div class="find-consultant-result-cell"><span class="find-consultant-result-label">' +
        FindConsultantComponent.escapeHtml(S.resultName || 'Name') +
        ':' +
        '</span>' +
        nameEsc +
        '</div>' +
        '<div class="find-consultant-result-cell"><span class="find-consultant-result-label">' +
        FindConsultantComponent.escapeHtml(S.resultState || 'State / Province') +
        ':' +
        '</span>' +
        stateEsc +
        '</div>' +
        '<div class="find-consultant-result-cell" >' +
        '<button type="button" class="btn btn-primary btn-sm" data-find-consultant-contact ' +
        'data-consultant-name="' +
        FindConsultantComponent.escapeAttr(row.name) +
        '" data-consultant-state="' +
        FindConsultantComponent.escapeAttr(row.state) +
        '" data-consultant-email="' +
        FindConsultantComponent.escapeAttr(row.email || '') +
        '">' +
        '<span class="text">' +
        FindConsultantComponent.escapeHtml(S.contactMe || 'Contact me') +
        '</span>' +
        '</button>' +
        '</div></div>';
      container.appendChild(wrap);
    });
    requestAnimationFrame(function () {
      FindConsultantComponent.updateResultsScrollState(container);
    });
  }

  static normalizeNearMeList(data) {
    if (Array.isArray(data)) return data;
    if (data && typeof data === 'object') {
      if (Array.isArray(data.consultants)) return data.consultants;
      if (Array.isArray(data.data)) return data.data;
      if (Array.isArray(data.results)) return data.results;
    }
    return [];
  }

  static rowFromNearMeItem(item) {
    if (item == null || typeof item !== 'object') return null;
    const nested = item.consultant || item.Consultant;
    let name =
      item.name ||
      item.fullName ||
      item.consultantName ||
      [item.firstName, item.lastName].filter(Boolean).join(' ').trim();
    if (!name && nested && typeof nested === 'object') {
      name =
        nested.name ||
        nested.fullName ||
        nested.consultantName ||
        [nested.firstName, nested.lastName].filter(Boolean).join(' ').trim();
    }
    if (!name) name = 'Consultant';
    let state =
      item.state || item.province || item.stateProvince || item.region || '';
    if (!state && nested && typeof nested === 'object') {
      state = nested.state || nested.province || nested.stateProvince || nested.region || '';
    }
    let email = FindConsultantComponent.pickConsultantEmailFromObject(item);
    if (!email && nested && typeof nested === 'object') {
      email = FindConsultantComponent.pickConsultantEmailFromObject(nested);
    }
    return { name: String(name), state: String(state), email: email };
  }

  static getNearResultsContainer(form) {
    const block = form.closest('.find-consultant-form-block');
    if (!block) return null;
    return block.querySelector('#FindConsultantNearResultsShell .find-consultant-results');
  }

  static getByNameResultsContainer(form) {
    const block = form.closest('.find-consultant-form-block');
    if (!block) return null;
    return block.querySelector('#FindConsultantByNameResultsShell .find-consultant-results');
  }

  static setNearFormBusy(form, busy) {
    Utility.setSubmitButtonLoading(form.querySelector('button[type="submit"]'), !!busy);
  }

  static setByNameFormBusy(form, busy) {
    Utility.setSubmitButtonLoading(form.querySelector('button[type="submit"]'), !!busy);
  }

  static getContactModalField(form, key) {
    const el = form.querySelector('[data-find-consultant-field="' + key + '"]');
    if (!el || !('value' in el)) return '';
    return String(el.value || '').trim();
  }

  /** Clears `[data-find-consultant-error]` / `[data-find-consultant-success]` in the contact modal form. */
  static clearContactModalFeedback(form) {
    const errEl = form.querySelector('[data-find-consultant-error]');
    const okEl = form.querySelector('[data-find-consultant-success]');
    if (errEl) {
      errEl.classList.add('d-none');
      errEl.textContent = '';
    }
    if (okEl) {
      okEl.classList.add('d-none');
      okEl.textContent = '';
    }
  }

  /**
   * @param {'error'|'success'} type
   */
  static showContactModalFeedback(form, type, message) {
    const errEl = form.querySelector('[data-find-consultant-error]');
    const okEl = form.querySelector('[data-find-consultant-success]');
    if (errEl) {
      errEl.classList.toggle('d-none', type !== 'error');
      errEl.textContent = type === 'error' ? message : '';
    }
    if (okEl) {
      okEl.classList.toggle('d-none', type !== 'success');
      okEl.textContent = type === 'success' ? message : '';
    }
  }

  static init() {
    if (window.__watkinsFindConsultantJsLoaded) {
      return;
    }
    window.__watkinsFindConsultantJsLoaded = true;

    const bindSections = function () {
      document.querySelectorAll('.find-consultant-section').forEach(function (root) {
        if (root.dataset.findConsultantBound === 'true') {
          return;
        }
        root.dataset.findConsultantBound = 'true';
        new FindConsultantSection(root).attach();
      });
    };

    const start = function () {
      bindSections();
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', start);
    } else {
      start();
    }

    let findConsultantResizeScrollTimer;
    window.addEventListener('resize', function () {
      clearTimeout(findConsultantResizeScrollTimer);
      findConsultantResizeScrollTimer = setTimeout(function () {
        document.querySelectorAll('.find-consultant-results').forEach(FindConsultantComponent.updateResultsScrollState);
      }, 150);
    });
  }
}

class FindConsultantSection {
  /**
   * @param {HTMLElement} root `.find-consultant-section`
   */
  constructor(root) {
    this.root = root;
    this._onSubmit = this.onSubmit.bind(this);
    this._onClick = this.onClick.bind(this);
    this._onInput = this.onInput.bind(this);
  }

  attach() {
    /** Capture phase so we always run before the browser’s default form submit (Shopify contact form POST). */
    this.root.addEventListener('submit', this._onSubmit, true);
    this.root.addEventListener('click', this._onClick);
    this.root.addEventListener('input', this._onInput);
    this.initMessageCounts();
    this.initByNameInputClear();
    this.initByNameCountryProvince();
  }

  /** US/CA country → state dropdown for by-name search (same as consultant-province). */
  initByNameCountryProvince() {
    const countryEl = this.root.querySelector('[data-consultant-search-country]');
    const stateEl = this.root.querySelector('[data-consultant-search-state]');
    if (!countryEl || !stateEl || !countryEl.id || !stateEl.id) return;
    const container =
      stateEl.closest('[id^="FindConsultantSearchProvinceContainer-"]') || stateEl.parentElement;
    Utility.initUsCaCountryProvinceSelector(
      countryEl.id,
      stateEl.id,
      container && container.id ? container.id : stateEl.id
    );
  }

  initMessageCounts() {
    const root = this.root;
    root.querySelectorAll('[id^="FindConsultantContactMessage-"]').forEach(function (ta) {
      const sid = ta.id.replace('FindConsultantContactMessage-', '');
      const counter =
        document.getElementById('FindConsultantMessageCount-' + sid) ||
        root.querySelector('#FindConsultantMessageCount-' + sid);
      if (counter) {
        counter.textContent = String(ta.value.length) + ' / 2000';
      }
    });
  }

  initByNameInputClear() {
    const form = this.root.querySelector('form.find-consultant-by-name-form');
    if (!form || form.dataset.findConsultantValidityClear === 'true') {
      return;
    }
    form.dataset.findConsultantValidityClear = 'true';
    form.querySelectorAll('input, select').forEach(function (input) {
      const ev = input.tagName === 'SELECT' ? 'change' : 'input';
      input.addEventListener(ev, function () {
        FindConsultantComponent.clearByNameFormValidity(form);
      });
    });
  }

  /**
   * @param {SubmitEvent} e
   */
  onSubmit(e) {
    const form = e.target;
    if (!form || form.tagName !== 'FORM' || !this.root.contains(form)) {
      return;
    }

    const S = FindConsultantComponent.strings;

    if (form.id === 'FindConsultantNearForm') {
      e.preventDefault();
      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }

      const addressEl = form.elements.find_near_address;
      const cityEl = form.elements.find_near_city;
      const stateEl = form.elements.find_near_state;
      const zipEl = form.elements.find_near_zip;

      const address = addressEl && addressEl.value ? addressEl.value.trim() : '';
      const city = cityEl && cityEl.value ? cityEl.value.trim() : '';
      const province = stateEl && stateEl.value ? String(stateEl.value).trim() : '';
      const postalCode = zipEl && zipEl.value ? zipEl.value.trim() : '';

      const params = new URLSearchParams();
      if (address) params.set('address', address);
      if (city) params.set('city', city);
      if (province) params.set('province', province);
      params.set('postalCode', postalCode);

      const results = FindConsultantComponent.getNearResultsContainer(form);
      if (!results) return;

      FindConsultantComponent.hideResultsShellForContainer(results);
      results.innerHTML = '';

      FindConsultantComponent.setNearFormBusy(form, true);

      fetch(NEAR_ME_PATH + '?' + params.toString(), {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      })
        .then(function (r) {
          if (!r.ok) throw new Error('near-me HTTP ' + r.status);
          return r.json();
        })
        .then(function (data) {
          const list = FindConsultantComponent.normalizeNearMeList(data);
          const rows = list.map(FindConsultantComponent.rowFromNearMeItem).filter(Boolean);
          if (!rows.length) {
            results.innerHTML =
              '<p class="small text-muted mb-0" role="status">' +
              FindConsultantComponent.escapeHtml(S.nearMeNoResults || 'No consultants found.') +
              '</p>';
            FindConsultantComponent.showResultsShellForContainer(results);
            FindConsultantComponent.updateResultsScrollState(results);
            return;
          }
          FindConsultantComponent.renderResultRows(results, rows, { showDemoNote: false });
        })
        .catch(function () {
          results.innerHTML =
            '<p class="small text-danger mb-0" role="alert">' +
            FindConsultantComponent.escapeHtml(S.nearMeError || 'Request failed.') +
            '</p>';
          FindConsultantComponent.showResultsShellForContainer(results);
          FindConsultantComponent.updateResultsScrollState(results);
        })
        .finally(function () {
          FindConsultantComponent.setNearFormBusy(form, false);
        });

      return;
    }

    if (form.id === 'FindConsultantByNameForm') {
      e.preventDefault();
      FindConsultantComponent.clearByNameFormValidity(form);

      const fd = new FormData(form);
      const first = String(fd.get('consultant_search_first') || '').trim();
      const last = String(fd.get('consultant_search_last') || '').trim();
      const stateEl = form.querySelector('[name="consultant_search_state"]');
      const stateLabel = Utility.getSelectedProvinceSearchValue(stateEl);

      if (!first && !last && !stateLabel) {
        const msg =
          S.byNameAtLeastOne || 'Enter at least one: last or business name, or state.';
        const anchorEl =
          form.querySelector('[name="consultant_search_last"]') ||
          form.querySelector('[name="consultant_search_state"]');
        if (anchorEl) {
          anchorEl.setCustomValidity(msg);
          anchorEl.reportValidity();
        }
        return;
      }

      const params = new URLSearchParams();
      if (first) params.set('firstName', first);
      if (last) params.set('lastName', last);
      if (stateLabel) params.set('province', stateLabel);

      const results = FindConsultantComponent.getByNameResultsContainer(form);
      if (!results) return;

      FindConsultantComponent.hideResultsShellForContainer(results);
      results.innerHTML = '';

      FindConsultantComponent.setByNameFormBusy(form, true);

      fetch(FindConsultantComponent.buildConsultantFindRequestUrl(params), {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      })
        .then(function (r) {
          if (!r.ok) throw new Error('find HTTP ' + r.status);
          return r.json();
        })
        .then(function (data) {
          const apiRows = FindConsultantComponent.extractFindApiRows(data, stateLabel);
          const rows = FindConsultantComponent.mapFindApiRowsToContactRows(apiRows);
          if (!rows.length) {
            results.innerHTML =
              '<p class="small text-muted mb-0" role="status">' +
              FindConsultantComponent.escapeHtml(S.byNameNoResults || 'No data found') +
              '</p>';
            FindConsultantComponent.showResultsShellForContainer(results);
            FindConsultantComponent.updateResultsScrollState(results);
            return;
          }
          FindConsultantComponent.renderResultRows(results, rows, { showDemoNote: false });
        })
        .catch(function () {
          results.innerHTML =
            '<p class="small text-danger mb-0" role="alert">' +
            FindConsultantComponent.escapeHtml(S.byNameError || 'Request failed.') +
            '</p>';
          FindConsultantComponent.showResultsShellForContainer(results);
          FindConsultantComponent.updateResultsScrollState(results);
        })
        .finally(function () {
          FindConsultantComponent.setByNameFormBusy(form, false);
        });

      return;
    }

    if (!form.classList.contains('find-consultant-contact-form')) {
      return;
    }
    if (!form.closest('.find-consultant-contact-modal')) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    FindConsultantComponent.clearContactModalFeedback(form);

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const firstName = FindConsultantComponent.getContactModalField(form, 'firstName');
    const lastName = FindConsultantComponent.getContactModalField(form, 'lastName');
    const email = FindConsultantComponent.getContactModalField(form, 'email');
    const message = FindConsultantComponent.getContactModalField(form, 'message');
    const consultantEmailEl = form.querySelector('.find-consultant-context-email');
    const consultantEmail = consultantEmailEl ? String(consultantEmailEl.value || '').trim() : '';

    const submitBtn = form.querySelector('.find-consultant-modal-actions button[type="submit"]');
    Utility.setSubmitButtonLoading(submitBtn, true);

    fetch(FindConsultantComponent.buildContactConsultantUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        firstName: firstName,
        lastName: lastName,
        email: email,
        consultantEmail: consultantEmail,
        message: message,
      }),
      credentials: 'same-origin',
    })
      .then(function (r) {
        if (!r.ok) {
          return r.text().then(function (t) {
            const err = new Error(t || 'HTTP ' + r.status);
            err.bodyText = t;
            err.status = r.status;
            throw err;
          });
        }
        const ct = (r.headers.get('Content-Type') || '').toLowerCase();
        if (ct.indexOf('application/json') !== -1) {
          return r.json();
        }
        return r.text();
      })
      .then(function () {
        FindConsultantComponent.showContactModalFeedback(
          form,
          'success',
          S.contactApiSuccess || 'Your message was sent. Thank you.'
        );
        form.reset();
        const hiddenName = form.querySelector('#FindConsultantContextName');
        const hiddenState = form.querySelector('#FindConsultantContextState');
        if (consultantEmailEl) consultantEmailEl.value = '';
        if (hiddenName) hiddenName.value = '';
        if (hiddenState) hiddenState.value = '';
      })
      .catch(function (err) {
        let msg = S.contactApiError || 'We could not send your message. Please try again.';
        const raw = err && err.bodyText;
        if (raw && typeof raw === 'string' && raw.trim()) {
          try {
            const j = JSON.parse(raw);
            if (j && typeof j === 'object') {
              const m = j.message || j.error || j.detail;
              if (m != null && String(m).trim()) msg = String(m).trim();
            }
          } catch (e) {
            if (raw.length < 400) msg = raw.trim();
          }
        }
        FindConsultantComponent.showContactModalFeedback(form, 'error', msg);
      })
      .finally(function () {
        Utility.setSubmitButtonLoading(submitBtn, false);
      });
  }

  /**
   * @param {MouseEvent} e
   */
  onClick(e) {
    const contactBtn = e.target.closest('[data-find-consultant-contact]');
    if (contactBtn && this.root.contains(contactBtn)) {
      const name = contactBtn.getAttribute('data-consultant-name') || '';
      const state = contactBtn.getAttribute('data-consultant-state') || '';
      const modal = this.root.querySelector('#ContactConsultantModal');
      if (!modal || typeof modal.show !== 'function') return;

      const hiddenName = this.root.querySelector('#FindConsultantContextName');
      const hiddenState = this.root.querySelector('#FindConsultantContextState');
      const hiddenEmail = this.root.querySelector('.find-consultant-context-email');
      if (hiddenName) hiddenName.value = name;
      if (hiddenState) hiddenState.value = state;
      if (hiddenEmail) hiddenEmail.value = contactBtn.getAttribute('data-consultant-email') || '';

      const contactForm = this.root.querySelector('form.find-consultant-contact-form');
      if (contactForm) {
        const modalSubmit = contactForm.querySelector('.find-consultant-modal-actions button[type="submit"]');
        Utility.setSubmitButtonLoading(modalSubmit, false);
        FindConsultantComponent.clearContactModalFeedback(contactForm);
      }
      const msg = contactForm && contactForm.querySelector('textarea');
      if (msg) {
        msg.value = '';
        msg.setCustomValidity('');
      }

      modal.show(contactBtn);
      return;
    }

    const cancel = e.target.closest('[data-find-consultant-modal-cancel]');
    if (cancel && this.root.contains(cancel)) {
      const modal = cancel.closest('modal-dialog');
      if (modal && typeof modal.hide === 'function') {
        modal.hide();
      }
    }
  }

  /**
   * @param {Event} e
   */
  onInput(e) {
    const ta = e.target;
    if (!ta || !ta.id || ta.id.indexOf('FindConsultantContactMessage-') !== 0) return;
    if (!this.root.contains(ta)) return;
    const sid = ta.id.replace('FindConsultantContactMessage-', '');
    const counter =
      document.getElementById('FindConsultantMessageCount-' + sid) ||
      this.root.querySelector('#FindConsultantMessageCount-' + sid);
    if (counter) {
      counter.textContent = String(ta.value.length) + ' / 2000';
    }
  }
}

globalThis.FindConsultantComponent = FindConsultantComponent;
FindConsultantComponent.init();
